import clsx from "clsx";
import { useCallback, useMemo, useRef, useState } from "react";

import { useOnClickOutside } from "@shared/hooks/use-onclick-outside";

import { Button } from "../button";
import { Icon } from "../icon";
import { Body } from "../typography";

import s from "./select.module.scss";

export type CompProps = React.ComponentPropsWithoutRef<"input"> & {
  options: string[];
  name: string;
  value?: string;
  onChange: (val: string) => void;
};

export const Select = (props: CompProps) => {
  const { options, name, value, onChange, ...inputProps } = props;

  const $rootRef = useRef<HTMLDivElement | null>(null);

  const [isOpenDropdown, setIsOpenDropdown] = useState(false);

  const toggleOpenDropdown = useCallback(() => {
    setIsOpenDropdown((prev) => !prev);
  }, []);

  const closeDropdown = () => {
    setIsOpenDropdown(false);
  };

  const currentSelection = useMemo(() => value || options[0], [value, options]);

  useOnClickOutside($rootRef, [s], closeDropdown);

  const handleSelect = (item: string) => {
    onChange?.(item);
    setIsOpenDropdown(false);
  };

  return (
    <div className={clsx(s.root)} ref={$rootRef}>
      <Button className={clsx(s.button)} onClick={toggleOpenDropdown}>
        <Body size="uppercase" weight="semiBold">
          {currentSelection}
        </Body>
        <Icon
          name="arrow-down"
          size={24}
          className={clsx(s.arrowIcon, { [s.rotate]: isOpenDropdown })}
        />
      </Button>
      <div
        className={clsx(s.moreList, "lenis-prevent", {
          [s.openDropdown]: isOpenDropdown,
        })}
      >
        {options.map((item) => (
          <label
            key={`${name}-${item}`}
            htmlFor={`${name}-${item}`}
            className={clsx(s.radio, { [s.active]: currentSelection === item })}
          >
            <input
              id={`${name}-${item}`}
              type="radio"
              value={item}
              name={name}
              checked={currentSelection === item}
              onChange={() => handleSelect(item)}
              {...inputProps}
            />
            <Body size="uppercase-small" weight="semiBold">
              {item}
            </Body>
          </label>
        ))}
      </div>
    </div>
  );
};

Select.displayName = "Select";
